import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  AlertTitle,
  Tooltip,
  TextField,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { DialogWrapper } from '../DialogWrapper';
import { useEscKey } from '../../../hooks/useEscKey';

interface EditTicketDialogProps {
  open: boolean;
  onClose: () => void;
  selectedTicket: any;
  editTicketForm: any;
  setEditTicketForm: (form: any) => void;
  files: File[];
  ticketFiles: any[];
  filesLoading: boolean;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>, existingFilesCount?: number) => void;
  onRemoveFile: (index: number) => void;
  onFilePreview: (file: any) => void;
  onFileDownload: (file: any) => void;
  onEditTicket: () => void;
  authUser: any;
  modules: any[];
  priorities: any[];
  onFileDelete: (fileId: number) => void; // ✅ YENİ: Dosya silme fonksiyonu
  showSnackbar: (message: string, severity: 'success' | 'error' | 'warning') => void; // 
}

export const EditTicketDialog: React.FC<EditTicketDialogProps> = ({
  open,
  onClose,
  selectedTicket,
  editTicketForm,
  setEditTicketForm,
  files,
  ticketFiles,
  filesLoading,
  onFileUpload,
  onRemoveFile,
  onFilePreview,
  onFileDownload,
  onEditTicket,
  authUser,
  modules,
  priorities,
  onFileDelete,
  showSnackbar,
}) => {
  useEscKey(open, onClose);
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(ext || '')) return '🖼️';
    if (ext === 'pdf') return '📄';
    if (['doc', 'docx'].includes(ext || '')) return '📝';
    if (['xls', 'xlsx'].includes(ext || '')) return '📊';
    if (['zip', 'rar'].includes(ext || '')) return '📦';
    return '📎';
  };

  const decodeFileName = (fileName: string): string => {
    try {
      if (!fileName) return 'dosya';
      
      try {
        if (fileName.match(/^[A-Za-z0-9+/]+={0,2}$/) && fileName.length % 4 === 0) {
          const decoded = atob(fileName);
          if (decoded) return decoded;
        }
      } catch (e) {}

      try {
        const decoded = decodeURIComponent(escape(fileName));
        if (decoded !== fileName) return decoded;
      } catch (e) {}

      let fixedName = fileName
        .replace(/Ã/g, 'Ç').replace(/Ã§/g, 'ç')
        .replace(/Ä/g, 'Ğ').replace(/Ä/g, 'ğ')
        .replace(/Ã/g, 'Ü').replace(/Ã¼/g, 'ü')
        .replace(/Å/g, 'Ş').replace(/Å/g, 'ş')
        .replace(/Ã/g, 'İ').replace(/Ã¶/g, 'ö')
        .replace(/Ã/g, 'Ö').replace(/Ã½/g, 'ı')
        .replace(/Ã/g, 'I').replace(/Ã°/g, 'İ');

      if (/[ÃÅ]/.test(fixedName)) {
        fixedName = fixedName
          .replace(/[^\x00-\x7F]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
      }

      return fixedName || 'dosya';
    } catch (error) {
      return fileName || 'dosya';
    }
  };

  const handleFormChange = (field: string, value: any) => {
    setEditTicketForm((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFileUpload(event, ticketFiles.length);
    event.target.value = '';
  };

  return (
    <DialogWrapper 
      open={open} 
      onClose={onClose}
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown={true}
      PaperProps={{
        sx: { 
          maxHeight: '95vh',
          marginLeft: '-30px',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          width: '800px'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#2c3e50',
        color: 'white',
        py: 2,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            ✏️ Talep Düzenle - #{selectedTicket?.id}
          </Typography>
          <IconButton 
            onClick={onClose}
            sx={{ 
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {authUser?.role === 'customer' && selectedTicket?.status_id !== 1 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle sx={{ fontSize: '0.9rem' }}>Talep Düzenlenemez</AlertTitle>
              <Typography sx={{ fontSize: '0.8rem' }}>
                Bu talep incelenmeye başlandığı için düzenleyemezsiniz.
              </Typography>
            </Alert>
          )}

          {authUser?.role === 'customer' && selectedTicket?.status_id === 1 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle sx={{ fontSize: '0.9rem' }}>Talep Henüz İncelenmedi</AlertTitle>
              <Typography sx={{ fontSize: '0.8rem' }}>
                Talebiniz henüz "Yeni" durumunda olduğu için düzenleyebilirsiniz.
              </Typography>
            </Alert>
          )}

          {/* Program */}
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontSize: '0.875rem' }}>Program *</InputLabel>
            <Select
              value={editTicketForm.module_id || ''}
              label="Program *"
              onChange={(e) => handleFormChange('module_id', e.target.value)}
              disabled={authUser?.role === 'customer' && selectedTicket?.status_id !== 1}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e0e0e0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1976d2',
                },
                '& .MuiSelect-select': {
                  padding: '10px 14px',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
            >
              {modules.map(module => (
                <MenuItem key={module.id} value={module.id} sx={{ fontSize: '0.875rem' }}>
                  {module.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Öncelik - Müşteri değilse */}
          {authUser?.role !== 'customer' && (
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '0.875rem' }}>Öncelik</InputLabel>
              <Select
                value={editTicketForm.priority_id || '1'}
                label="Öncelik"
                onChange={(e) => handleFormChange('priority_id', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                  '& .MuiSelect-select': {
                    padding: '10px 14px',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center'
                  }
                }}
              >
                {priorities.map((priority) => (
                  <MenuItem key={priority.id} value={priority.id} sx={{ fontSize: '0.875rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span style={{ fontSize: '14px' }}>
                        {priority.id === 1 ? '🟢' : 
                         priority.id === 2 ? '🔵' : 
                         priority.id === 3 ? '🟠' : '🔴'}
                      </span>
                      <span>
                        {priority.id === 1 ? 'Düşük' : 
                         priority.id === 2 ? 'Orta' : 
                         priority.id === 3 ? 'Yüksek' : 'Acil'}
                      </span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Grid container spacing={2}>
            {/* E-posta */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="E-posta"
                value={editTicketForm.email || ''}
                onChange={(e) => handleFormChange('email', e.target.value)}
                error={editTicketForm.email !== '' && !isValidEmail(editTicketForm.email)}
                helperText={editTicketForm.email !== '' && !isValidEmail(editTicketForm.email) ? 'Geçerli bir e-posta adresi giriniz' : ''}
                placeholder="musteri@firma.com"
                disabled={authUser?.role === 'customer' && selectedTicket?.status_id !== 1}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem'
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                    padding: '10px 14px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Grid>

            {/* Talep Konusu */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Talep Konusu *"
                value={editTicketForm.subject || ''}
                onChange={(e) => handleFormChange('subject', e.target.value)}
                placeholder="Kısa ve net bir konu giriniz..."
                disabled={authUser?.role === 'customer' && selectedTicket?.status_id !== 1}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem'
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                    padding: '10px 14px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center'
                  }
                }}
              />
            </Grid>
          </Grid>

          {/* Talep Detayları */}
          <TextField
            fullWidth
            multiline
            rows={5}
            label="Talep Detayları"
            value={editTicketForm.mail_content || ''}
            onChange={(e) => handleFormChange('mail_content', e.target.value)}
            placeholder="Talep detaylarınızı buraya yazınız..."
            disabled={authUser?.role === 'customer' && selectedTicket?.status_id !== 1}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#1976d2',
                    },
                    padding: '0', 
                  },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem'
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                padding: '12px 14px'
              }
            }}
          />

          {/* Aksan Yazılım Tarafından Sorunun Açıklaması - Müşteri değilse */}
          {authUser?.role !== 'customer' && (
            <TextField
              fullWidth
              multiline
              rows={5}
              label="Aksan Yazılım Tarafından Sorunun Açıklaması"
              value={editTicketForm.description || ''}
              onChange={(e) => handleFormChange('description', e.target.value)}
              placeholder="Sorunu detaylı bir şekilde açıklayın..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                    },
                    padding: '0', 
                  },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem'
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem',
                  padding: '12px 14px'
                }
              }}
            />
          )}

          {/* Dosya Ekleme Bölümü */}
          <Box sx={{ 
            p: 2, 
            border: '1px dashed', 
            borderColor: 'grey.300', 
            borderRadius: 1,
            backgroundColor: '#fafafa'
          }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#333', fontSize: '0.9rem' }}>
              📎 Dosya Ekle (Opsiyonel)
              {ticketFiles.length > 0 && (
                <Typography component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
                  (Mevcut: {ticketFiles.length}/10)
                </Typography>
              )}
            </Typography>

            <Button
              variant="outlined"
              component="label"
              startIcon={<AttachFileIcon />}
              size="small"
              disabled={
                (authUser?.role === 'customer' && selectedTicket?.status_id !== 1) || 
                (ticketFiles.length + files.length >= 5)
              }
              sx={{ 
                mb: 2,
                borderColor: '#1976d2',
                color: '#1976d2',
                fontSize: '0.8rem',
                padding: '6px 12px',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              DOSYA SEÇ
              <input
                type="file"
                hidden
                multiple
                onChange={handleFileSelect}
                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.txt,.csv"
              />
            </Button>

            <Typography variant="caption" display="block" color="text.secondary" sx={{ lineHeight: 1.4, fontSize: '0.75rem' }}>
              Maksimum 10 dosya, her dosya max 10MB. İzin verilen formatlar: 
              Resim (JPG, PNG, GIF), Döküman (PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX), 
              Arşiv (ZIP, RAR), Metin (TXT, CSV)
            </Typography>

            {/* Seçilen Dosyalar */}
            {files.length > 0 && (
              <Box sx={{ mt: 2 }}>
                {files.map((file, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography sx={{ fontSize: '18px' }}>
                        {getFileIcon(file.name)}
                      </Typography>
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ color: '#333', fontSize: '0.8rem' }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {formatFileSize(file.size)}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton 
                      size="small" 
                      onClick={() => onRemoveFile(index)}
                      color="error"
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(211, 47, 47, 0.04)'
                        },
                        padding: '4px'
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Chip 
                  label={`${files.length}/10 dosya seçildi`} 
                  color="primary" 
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1, fontWeight: 500, fontSize: '0.7rem', height: '24px' }}
                />
              </Box>
            )}
          </Box>

          {/* Mevcut Ekler */}
          {ticketFiles.length > 0 && (
            <Box sx={{ 
              p: 2, 
              border: '1px solid', 
              borderColor: 'grey.200', 
              borderRadius: 1,
              backgroundColor: '#f8f9fa'
            }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#333', fontSize: '0.9rem' }}>
                📎 Mevcut Ekler
              </Typography>
              <List dense>
                {ticketFiles.map((f) => (
                  <ListItem
                    key={f.id}
                    secondaryAction={
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Önizle">
                          <IconButton
                            size="small"
                            onClick={() => onFilePreview(f)}
                            sx={{ color: 'primary.main' }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="İndir">
                          <IconButton
                            size="small"
                            onClick={() => onFileDownload(f)}
                            sx={{ color: 'success.main' }}
                          >
                            <FileDownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                          {/* ✅ YENİ: Silme butonu */}
                            <Tooltip title="Sil">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (window.confirm('Bu dosyayı silmek istediğinizden emin misiniz?')) {
                                    onFileDelete(f.id);
                                  }
                                }}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                      </Box>
                    }
                    sx={{ 
                      py: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Typography sx={{ fontSize: '16px' }}>
                        {getFileIcon(f.name)}
                      </Typography>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          {decodeFileName(f.name)}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {formatFileSize(f.size)}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, gap: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ 
            minWidth: 100,
            borderColor: '#666',
            color: '#666',
            fontSize: '0.8rem',
            padding: '6px 16px',
            '&:hover': {
              borderColor: '#333',
              backgroundColor: 'rgba(0,0,0,0.04)'
            }
          }}
        >
          İPTAL
        </Button>
        <Button 
          variant="contained" 
          onClick={onEditTicket}
          disabled={!editTicketForm.module_id || !editTicketForm.subject || (authUser?.role === 'customer' && selectedTicket?.status_id !== 1)}
          sx={{ 
            minWidth: 120,
            backgroundColor: '#2c3e50',
            fontSize: '0.8rem',
            padding: '6px 16px',
            '&:hover': {
              backgroundColor: '#1a252f'
            },
            '&:disabled': {
              backgroundColor: '#b0b0b0'
            }
          }}
        >
          GÜNCELLE
        </Button>
      </DialogActions>
    </DialogWrapper>
  );
};